import React, { useEffect, useState, useMemo, useCallback } from 'react';
import * as api from '../../services/api';
import {
  Shield, ShieldCheck, AlertTriangle, Loader, CheckCircle, X, Clock,
  Hash, Filter, Activity, FileText, ChevronDown, ChevronRight, Lock,
  Eye, Database, Play, Zap, RefreshCw,
} from 'lucide-react';

/**
 * WorkflowEnforcementAuditPanel — per-run 7-stage enforcement timeline.
 *
 * Source data: /api/v1/workflows/runs/{runId}/enforcement-chain
 * (aggregates policy_audit_events + governance_audit_envelopes into a
 * uniform stage-keyed shape). Same shape for native and GEL workflows.
 *
 * Visual: matches the Workflow Discovery → Audit screenshot — accordion
 * per run, 7-stop timeline (PRE-GATE → DPG SCAN → POLICY EVAL → DECISION
 * → EXECUTION → AUDIT → POST-RECORD), enforcement detail with reason /
 * mode / classification, current/previous hash, ALL/ALLOW/BLOCK/WARN
 * filter pills.
 */

const COLORS = {
  S: { 0: '#fff', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded', 200: '#e7e5e4',
    300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c', 600: '#57534e',
    700: '#44403c', 800: '#292524', 900: '#1c1917' },
  brand: { primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1', soft: '#f8d7d4' },
  emerald: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:     { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:    { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:  { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:   { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
};

// 7-stop canonical order. Matches the backend's STAGES list and
// CanvasWorkflowMaterializer's GEL DAG layout.
const STAGE_ORDER = ['PRE_GATE', 'DPG_SCAN', 'POLICY_EVAL', 'DECISION',
                     'EXECUTION', 'POST_AUDIT', 'POST_RECORD'];

const STAGE_META = {
  PRE_GATE:    { label: 'Pre-Gate',    Icon: Lock,       short: '1' },
  DPG_SCAN:    { label: 'DPG Scan',    Icon: Eye,        short: '2' },
  POLICY_EVAL: { label: 'Policy Eval', Icon: Shield,     short: '3' },
  DECISION:    { label: 'Decision',    Icon: CheckCircle, short: '4' },
  EXECUTION:   { label: 'Execution',   Icon: Play,       short: '5' },
  POST_AUDIT:  { label: 'Audit',       Icon: FileText,   short: '6' },
  POST_RECORD: { label: 'Post-Record', Icon: Database,   short: '7' },
};

const STATUS_COLOR = {
  passed:  COLORS.emerald,
  warned:  COLORS.amber,
  blocked: COLORS.rose,
  skipped: COLORS.slate,
};

export default function WorkflowEnforcementAuditPanel({ workflowId, runs = [] }) {
  // We page through the parent's run list. Each run gets its own enforcement
  // chain fetched on demand. Cached so re-expanding doesn't refetch.
  const [chainByRun, setChainByRun] = useState({});
  const [loadingRunIds, setLoadingRunIds] = useState(new Set());
  const [errorByRun, setErrorByRun] = useState({});
  const [expanded, setExpanded] = useState(() => new Set(runs.length > 0 ? [runs[0].id] : []));
  const [filter, setFilter] = useState('ALL');

  // Build a quick lookup so loadChain can pick the right backend route based
  // on whether the run is native (UUID-keyed) or external (string-keyed).
  // Native runs go to /workflows/runs/{runId}/enforcement-chain; external
  // runs go to /workflows/external-runs/enforcement-chain?externalRunId=…
  const runMeta = React.useMemo(() => {
    const m = new Map();
    for (const r of runs) m.set(r.id, r);
    return m;
  }, [runs]);

  const loadChain = useCallback(async (runId) => {
    if (!runId || chainByRun[runId]) return;
    setLoadingRunIds((s) => new Set([...s, runId]));
    try {
      const meta = runMeta.get(runId);
      const isExternal = meta && (meta.source === 'EXTERNAL' || meta.isExternal === true);
      const res = isExternal
        ? await api.getExternalRunEnforcementChain(runId)
        : await api.getRunEnforcementChain(runId);
      if (res.ok) {
        setChainByRun((c) => ({ ...c, [runId]: res.data }));
        setErrorByRun((e) => ({ ...e, [runId]: null }));
      } else {
        setErrorByRun((e) => ({ ...e, [runId]: res.data?.message || 'Failed to load' }));
      }
    } catch (e) {
      setErrorByRun((er) => ({ ...er, [runId]: e.message }));
    } finally {
      setLoadingRunIds((s) => { const next = new Set(s); next.delete(runId); return next; });
    }
  }, [chainByRun, runMeta]);

  // Auto-load the first expanded run on mount so the panel isn't empty.
  useEffect(() => {
    if (runs.length > 0) loadChain(runs[0].id);
  }, [runs, loadChain]);

  const toggleRun = (runId) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(runId)) next.delete(runId);
      else { next.add(runId); loadChain(runId); }
      return next;
    });
  };

  const filteredRuns = useMemo(() => {
    if (filter === 'ALL') return runs;
    return runs.filter((r) => {
      const chain = chainByRun[r.id];
      if (!chain) return true;
      const decision = overallDecision(chain.stages || []);
      return decision === filter;
    });
  }, [runs, filter, chainByRun]);

  if (!workflowId) return null;
  if (!runs || runs.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: COLORS.S[500],
        background: COLORS.S[50], borderRadius: 12, border: `1px dashed ${COLORS.S[200]}` }}>
        <Activity size={28} color={COLORS.S[300]} />
        <div style={{ fontSize: 14, fontWeight: 600, marginTop: 12, color: COLORS.S[700] }}>
          No runs yet
        </div>
        <div style={{ fontSize: 12, marginTop: 4 }}>
          Trigger an execution to populate the enforcement audit timeline.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header + filter pills */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Activity size={16} color={COLORS.brand.primary} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.S[900] }}>
              Enforcement Records by Workflow
            </div>
            <div style={{ fontSize: 11, color: COLORS.S[500], marginTop: 2 }}>
              7-stage protection timeline per run · same shape for native and GEL workflows
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['ALL', 'passed', 'warned', 'blocked'].map((f) => {
            const active = filter === f;
            const color = f === 'passed' ? COLORS.emerald
                      : f === 'warned' ? COLORS.amber
                      : f === 'blocked' ? COLORS.rose
                      : COLORS.slate;
            const label = f === 'ALL' ? 'ALL' : f === 'passed' ? 'ALLOW' : f === 'warned' ? 'WARN' : 'BLOCK';
            return (
              <button key={f} onClick={() => setFilter(f)}
                style={{
                  padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.04em', cursor: 'pointer',
                  border: active ? `1px solid ${color.icon}` : `1px solid ${COLORS.S[200]}`,
                  background: active
                    ? (f === 'ALL' ? COLORS.S[900] : color.bg)
                    : '#fff',
                  color: active
                    ? (f === 'ALL' ? '#fff' : color.text)
                    : COLORS.S[600],
                }}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Per-run accordion */}
      {filteredRuns.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: COLORS.S[500], fontSize: 13 }}>
          No runs match the {filter} filter.
        </div>
      ) : (
        filteredRuns.map((run) => (
          <RunAccordion
            key={run.id}
            run={run}
            chain={chainByRun[run.id]}
            loading={loadingRunIds.has(run.id)}
            error={errorByRun[run.id]}
            expanded={expanded.has(run.id)}
            onToggle={() => toggleRun(run.id)}
            onRefresh={() => {
              setChainByRun((c) => { const next = { ...c }; delete next[run.id]; return next; });
              loadChain(run.id);
            }}
          />
        ))
      )}
    </div>
  );
}

/* ─── RunAccordion ──────────────────────────────────────────────────── */
function RunAccordion({ run, chain, loading, error, expanded, onToggle, onRefresh }) {
  const overall = chain ? overallDecision(chain.stages || []) : 'pending';
  const overallColor = overall === 'passed' ? COLORS.emerald
                    : overall === 'warned' ? COLORS.amber
                    : overall === 'blocked' ? COLORS.rose
                    : COLORS.slate;
  const startedAt = run.startedAt ? new Date(run.startedAt).toLocaleString()
                                  : run.createdAt ? new Date(run.createdAt).toLocaleString()
                                  : '—';

  return (
    <div style={{
      marginBottom: 12, background: '#fff', borderRadius: 12,
      border: `1px solid ${overall === 'blocked' ? COLORS.rose.border
                          : overall === 'warned' ? COLORS.amber.border
                          : COLORS.S[200]}`,
      borderLeft: `4px solid ${overallColor.icon}`,
      overflow: 'hidden',
    }}>
      {/* Run header */}
      <div onClick={onToggle} style={{
        padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12,
        cursor: 'pointer',
      }}>
        {expanded ? <ChevronDown size={16} color={COLORS.S[500]} /> : <ChevronRight size={16} color={COLORS.S[500]} />}
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: overallColor.bg, border: `2px solid ${overallColor.icon}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {overall === 'passed' && <CheckCircle size={14} color={overallColor.icon} />}
          {overall === 'warned' && <AlertTriangle size={14} color={overallColor.icon} />}
          {overall === 'blocked' && <X size={14} color={overallColor.icon} />}
          {overall === 'pending' && <Clock size={14} color={overallColor.icon} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.S[900] }}>
              Run · {String(run.id).slice(0, 8)}
            </span>
            <span style={{
              padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
              letterSpacing: '0.04em', textTransform: 'uppercase',
              background: overallColor.bg, color: overallColor.text,
              border: `1px solid ${overallColor.border}`,
            }}>
              {overall === 'passed' ? 'ALLOW' : overall === 'warned' ? 'WARN' : overall === 'blocked' ? 'BLOCK' : '—'}
            </span>
            {chain && (
              <span style={{ fontSize: 11, color: COLORS.S[500] }}>
                {chain.eventCount || 0} events · {chain.envelopeCount || 0} envelopes
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: COLORS.S[500], marginTop: 2 }}>{startedAt}</div>
        </div>
        {expanded && (
          <button onClick={(e) => { e.stopPropagation(); onRefresh(); }}
            title="Refresh chain"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: COLORS.S[500], padding: 4 }}>
            <RefreshCw size={14} />
          </button>
        )}
      </div>

      {expanded && (
        <div style={{ padding: '0 18px 16px 18px', borderTop: `1px solid ${COLORS.S[150]}` }}>
          {loading && (
            <div style={{ padding: 24, textAlign: 'center', color: COLORS.S[500] }}>
              <Loader size={18} style={{ animation: 'wfeSpin 0.8s linear infinite' }} />
              <div style={{ marginTop: 8, fontSize: 12 }}>Loading enforcement chain…</div>
              <style>{`@keyframes wfeSpin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
            </div>
          )}
          {error && (
            <div style={{ padding: '12px 14px', background: COLORS.rose.bg, color: COLORS.rose.text,
              border: `1px solid ${COLORS.rose.border}`, borderRadius: 8, fontSize: 12, marginTop: 12 }}>
              {error}
            </div>
          )}
          {chain && <ChainTimeline chain={chain} />}
        </div>
      )}
    </div>
  );
}

/* ─── ChainTimeline ─────────────────────────────────────────────────── */
function ChainTimeline({ chain }) {
  const stages = chain.stages || [];

  return (
    <>
      {/* The 7-stop timeline */}
      <div style={{
        marginTop: 18, padding: '20px 8px',
        background: COLORS.S[50], borderRadius: 10,
        border: `1px solid ${COLORS.S[200]}`,
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.S[600],
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14, paddingLeft: 12 }}>
          Enforcement Chain Timeline
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '0 8px', position: 'relative' }}>
          {STAGE_ORDER.map((stageKey, i) => {
            const meta = STAGE_META[stageKey];
            const stage = stages.find((s) => s.stage === stageKey) || { stage: stageKey, status: 'skipped' };
            const color = STATUS_COLOR[stage.status] || COLORS.slate;
            const isLast = i === STAGE_ORDER.length - 1;
            const Icon = meta.Icon;
            return (
              <React.Fragment key={stageKey}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                  flex: '0 0 auto', minWidth: 90, position: 'relative' }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: '50%',
                    background: color.bg,
                    border: `2px solid ${color.icon}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: color.icon, fontWeight: 800, fontSize: 14,
                    boxShadow: stage.status === 'passed' ? `0 0 0 4px ${color.bg}` : 'none',
                    fontFeatureSettings: "'tnum'",
                  }}>
                    {stage.status === 'skipped' ? meta.short
                      : stage.status === 'passed' ? <CheckCircle size={18} />
                      : stage.status === 'warned' ? <AlertTriangle size={18} />
                      : stage.status === 'blocked' ? <X size={18} />
                      : <Icon size={18} />}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 10, fontWeight: 700,
                    color: COLORS.S[700], textTransform: 'uppercase', letterSpacing: '0.06em',
                    textAlign: 'center' }}>
                    {meta.label}
                  </div>
                  {stage.durationMs > 0 && (
                    <div style={{ fontSize: 9, color: COLORS.S[500], marginTop: 2 }}>
                      {stage.durationMs}ms
                    </div>
                  )}
                </div>
                {!isLast && (
                  <div style={{ flex: 1, height: 2, marginTop: 19,
                    background: connectorColor(stage.status),
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Hash chain */}
      {(chain.stages || []).find((s) => s.currentHash) && (
        <HashChainStrip stages={chain.stages} integrityVerified={chain.integrityVerified} />
      )}

      {/* Per-stage detail */}
      <div style={{ marginTop: 16 }}>
        {stages
          .filter((s) => s.status !== 'skipped' || (s.events || []).length > 0)
          .map((s) => <StageDetail key={s.stage} stage={s} />)}
      </div>
    </>
  );
}

function HashChainStrip({ stages, integrityVerified }) {
  const lastWithHash = [...stages].reverse().find((s) => s.currentHash);
  if (!lastWithHash) return null;
  return (
    <div style={{
      marginTop: 12, padding: '12px 16px', background: '#fff',
      border: `1px solid ${COLORS.S[200]}`, borderRadius: 10,
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
    }}>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.S[600],
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
          display: 'flex', alignItems: 'center', gap: 6 }}>
          <Hash size={11} /> Current Hash
          {integrityVerified === true && (
            <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: COLORS.emerald.text,
              padding: '1px 6px', borderRadius: 4, background: COLORS.emerald.bg }}>
              ✓ chain verified
            </span>
          )}
          {integrityVerified === false && (
            <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: COLORS.rose.text,
              padding: '1px 6px', borderRadius: 4, background: COLORS.rose.bg }}>
              ✗ chain broken
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          color: COLORS.S[800], wordBreak: 'break-all' }}>
          {lastWithHash.currentHash || '—'}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.S[600],
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
          Previous Hash
        </div>
        <div style={{ fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          color: COLORS.S[800], wordBreak: 'break-all' }}>
          {lastWithHash.previousHash || '(genesis)'}
        </div>
      </div>
    </div>
  );
}

function StageDetail({ stage }) {
  if (stage.status === 'skipped' && (stage.events || []).length === 0) return null;
  const meta = STAGE_META[stage.stage];
  const color = STATUS_COLOR[stage.status] || COLORS.slate;
  return (
    <div style={{
      marginBottom: 8, padding: '12px 14px',
      background: '#fff', borderRadius: 10,
      border: `1px solid ${color.border}`,
      borderLeft: `4px solid ${color.icon}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: color.text,
          textTransform: 'uppercase', letterSpacing: '0.06em',
          padding: '2px 8px', borderRadius: 4, background: color.bg, border: `1px solid ${color.border}` }}>
          {meta?.label || stage.stage}
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, color: color.icon, textTransform: 'uppercase' }}>
          {stage.status}
        </span>
        {stage.durationMs > 0 && (
          <span style={{ fontSize: 10, color: COLORS.S[500], marginLeft: 'auto' }}>
            {stage.durationMs}ms
          </span>
        )}
      </div>
      {stage.reason && (
        <div style={{ fontSize: 12, color: COLORS.S[700], marginTop: 4 }}>
          <strong style={{ color: COLORS.S[800] }}>Reason:</strong> {stage.reason}
        </div>
      )}
      {(stage.events || []).length > 0 && (
        <div style={{ marginTop: 8, fontSize: 11, color: COLORS.S[600] }}>
          <span style={{ fontWeight: 700, color: COLORS.S[700] }}>Events:</span> {stage.events.length}
          {stage.events.slice(0, 3).map((e) => (
            <div key={e.id} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, color: COLORS.S[500], marginTop: 2 }}>
              {e.eventType} → {e.decision}{e.ruleId ? ` · rule ${String(e.ruleId).slice(0, 8)}` : ''}
            </div>
          ))}
          {stage.events.length > 3 && (
            <div style={{ fontSize: 10, color: COLORS.S[500], marginTop: 2 }}>
              + {stage.events.length - 3} more
            </div>
          )}
        </div>
      )}
      {/* EXECUTION stage gets the per-node table the operator is asking for.
          For native runs these are the actual workflow_node_executions rows;
          for external runs they're synthesized from the GEL inline-task list
          on the EXECUTION envelope (best signal we have for "what ran inside
          the wrapped workflow"). The same component renders both shapes. */}
      {stage.stage === 'EXECUTION' && (stage.nodeExecutions || []).length > 0 && (
        <NodeExecutionTable nodes={stage.nodeExecutions} />
      )}
    </div>
  );
}

/* ─── NodeExecutionTable ────────────────────────────────────────────────
   Per-node pass/fail/duration table rendered inside the EXECUTION stage.
   Status colors mirror the run-level decision palette (emerald/amber/rose/
   slate) so a quick scan tells you exactly which step in the DAG failed
   and how long it took.

   The shape passed in matches WorkflowEnforcementChainService.shrinkNodeExecutions:
     {id, nodeId, nodeType, status, durationMs, startedAt, completedAt, error}
*/
function NodeExecutionTable({ nodes }) {
  const NODE_STATUS_COLOR = {
    completed:        COLORS.emerald,
    failed:           COLORS.rose,
    skipped:          COLORS.slate,
    running:          COLORS.sky,
    pending:          COLORS.slate,
    awaiting_approval: COLORS.amber,
  };
  // Aggregate stats so the table header can show a quick rollup.
  const total = nodes.length;
  const failed = nodes.filter((n) => (n.status || '').toLowerCase() === 'failed').length;
  const totalMs = nodes.reduce((acc, n) => acc + (n.durationMs || 0), 0);

  return (
    <div style={{ marginTop: 12, border: `1px solid ${COLORS.S[200]}`,
      borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <div style={{
        padding: '8px 12px', background: COLORS.S[50],
        borderBottom: `1px solid ${COLORS.S[200]}`,
        display: 'flex', alignItems: 'center', gap: 10, fontSize: 11,
      }}>
        <span style={{ fontWeight: 700, color: COLORS.S[700], textTransform: 'uppercase',
          letterSpacing: '0.06em', fontSize: 10 }}>
          Node Executions
        </span>
        <span style={{ color: COLORS.S[500] }}>
          {total} node{total !== 1 ? 's' : ''}
          {failed > 0 && <span style={{ color: COLORS.rose.text, marginLeft: 6, fontWeight: 600 }}>
            · {failed} failed
          </span>}
        </span>
        <span style={{ marginLeft: 'auto', color: COLORS.S[500], fontVariantNumeric: 'tabular-nums' }}>
          Σ {totalMs}ms
        </span>
      </div>
      <div style={{ display: 'grid',
        gridTemplateColumns: 'minmax(180px,2fr) minmax(120px,1.2fr) 90px 80px',
        fontSize: 11 }}>
        {/* Header row */}
        <div style={hdrCellStyle}>Node</div>
        <div style={hdrCellStyle}>Type</div>
        <div style={{ ...hdrCellStyle, textAlign: 'center' }}>Status</div>
        <div style={{ ...hdrCellStyle, textAlign: 'right' }}>Duration</div>
        {nodes.map((n) => {
          const sc = NODE_STATUS_COLOR[(n.status || '').toLowerCase()] || COLORS.slate;
          const status = (n.status || 'pending').toLowerCase();
          return (
            <React.Fragment key={n.id || n.nodeId}>
              <div style={cellStyle}>
                <div style={{ fontWeight: 600, color: COLORS.S[800],
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontFamily: "'IBM Plex Mono', 'SF Mono', monospace", fontSize: 11 }}>
                  {n.nodeId}
                </div>
                {n.error && (
                  <div style={{ fontSize: 10, color: COLORS.rose.text, marginTop: 2,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={n.error}>
                    {n.error}
                  </div>
                )}
              </div>
              <div style={{ ...cellStyle, color: COLORS.S[600], fontFamily: "'IBM Plex Mono', monospace" }}>
                {n.nodeType}
              </div>
              <div style={{ ...cellStyle, textAlign: 'center' }}>
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                  fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
                  background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                  textTransform: 'uppercase',
                }}>
                  {status}
                </span>
              </div>
              <div style={{ ...cellStyle, textAlign: 'right',
                color: COLORS.S[700], fontVariantNumeric: 'tabular-nums',
                fontFamily: "'IBM Plex Mono', monospace" }}>
                {n.durationMs != null ? `${n.durationMs} ms` : '—'}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

const hdrCellStyle = {
  padding: '6px 12px', fontSize: 9, fontWeight: 700, color: COLORS.S[500],
  textTransform: 'uppercase', letterSpacing: '0.06em',
  background: COLORS.S[50], borderBottom: `1px solid ${COLORS.S[200]}`,
};

const cellStyle = {
  padding: '8px 12px', borderBottom: `1px solid ${COLORS.S[100]}`,
  display: 'flex', flexDirection: 'column', justifyContent: 'center',
};

function overallDecision(stages) {
  if (!stages || stages.length === 0) return 'pending';
  if (stages.some((s) => s.status === 'blocked')) return 'blocked';
  if (stages.some((s) => s.status === 'warned')) return 'warned';
  if (stages.every((s) => s.status === 'skipped')) return 'pending';
  return 'passed';
}

function connectorColor(status) {
  if (status === 'passed') return COLORS.emerald.icon;
  if (status === 'warned') return COLORS.amber.icon;
  if (status === 'blocked') return COLORS.rose.icon;
  return COLORS.S[300];
}
